/**
 * CookieConsent.tsx  -  GDPR/ePrivacy consent banner.
 * Gates first-party analytics. Essential sign-in cookies are exempt and always on.
 * Mounted once globally in App.
 */

import { useState, useEffect } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { isChildRoute } from '@/utils/analytics'

const CONSENT_KEY = 'pq_cookie_consent'

export function hasAnalyticsConsent(): boolean {
  try {
    return localStorage.getItem(CONSENT_KEY) === 'accepted'
  } catch {
    return false
  }
}

export default function CookieConsent() {
  const [visible, setVisible] = useState(false)
  const { pathname } = useLocation()

  useEffect(() => {
    try {
      if (!localStorage.getItem(CONSENT_KEY)) setVisible(true)
    } catch { /* storage blocked  -  don't show */ }
  }, [])

  function choose(value: 'accepted' | 'declined') {
    try { localStorage.setItem(CONSENT_KEY, value) } catch { /* ignore */ }
    setVisible(false)
    // Let analytics pick up the new consent immediately
    window.dispatchEvent(new CustomEvent('pq:consent', { detail: value }))
  }

  if (!visible || isChildRoute(pathname)) return null

  return (
    <div className="fixed inset-x-0 bottom-0 z-[100] p-3 sm:p-4">
      <div className="mx-auto flex max-w-3xl flex-col items-start gap-4 rounded-[18px] bg-brand-ink p-5 text-white shadow-panel sm:flex-row sm:items-center">
        <div className="flex-1 text-sm leading-relaxed">
          <p className="mb-1 font-bold">Cookies and privacy</p>
          <p className="text-white/75">
            We use an essential cookie to keep you signed in, and optional first-party
            analytics to improve the product. No ads, no third-party trackers.{' '}
            <Link to="/cookies" className="underline hover:text-white">Cookie policy</Link>.
          </p>
        </div>
        <div className="flex w-full flex-shrink-0 gap-2 sm:w-auto">
          <button
            onClick={() => choose('declined')}
            className="flex-1 rounded-full border border-white/40 px-5 py-2 text-sm font-semibold hover:bg-white/10 sm:flex-none"
          >
            Decline analytics
          </button>
          <button
            onClick={() => choose('accepted')}
            className="flex-1 rounded-full bg-brand-yellow px-5 py-2 text-sm font-semibold text-brand-ink hover:bg-sun-300 sm:flex-none"
          >
            Accept analytics
          </button>
        </div>
      </div>
    </div>
  )
}
